export function buildPieData(
  data: any[],
  groupByKey: string,
  colorFn: (key: string) => string,
): { name: string; value: number; color: string }[] {
  const counts: Record<string, number> = {};
  data.forEach(item => {
    const key = item[groupByKey];
    if (key == null) return;
    const k = String(key);
    counts[k] = (counts[k] ?? 0) + 1;
  });
  return Object.entries(counts).map(([name, value]) => ({
    name,
    value,
    color: colorFn(name),
  }));
}

function sortPeriods(periods: string[]): string[] {
  return [...periods].sort((a, b) => {
    const parse = (s: string) => {
      const [m, y] = s.split('-');
      const mn = parseInt(m, 10), yn = parseInt(y, 10);
      return isNaN(mn) || isNaN(yn) ? 0 : yn * 100 + mn;
    };
    return parse(a) - parse(b);
  });
}

export function buildLineData(
  data: any[],
  groupByKey: string,
  colorFn: (index: number) => string,
  periodKey: string = 'Period',
): { labels: string[]; datasets: any[]; legend: { name: string }[] } {
  const seriesValues = [...new Set(data.map(item => item[groupByKey]).filter(v => v != null))] as string[];
  const rawPeriods = [...new Set(data.map(item => item[periodKey]).filter(v => v != null))] as string[];
  const periods = sortPeriods(rawPeriods);

  const datasets = seriesValues.map((series, index) => ({
    data: periods.map(period =>
      data.filter(item => item[periodKey] === period && item[groupByKey] === series).length,
    ),
    strokeWidth: 4,
    color: (opacity = 1) => colorFn(index),
  }));

  return {
    labels: periods,
    datasets,
    legend: seriesValues.map(name => ({ name })),
  };
}

export function buildPeriodTable(
  data: any[],
  groupByKey: string,
  columnChoices: string[] | null,
  periodKey: string = 'Period',
  statusField: string | null = null,
): { tableData: any[]; columnKeys: { ColumnName: string; Order: number }[] } {
  const periods = [...new Set(data.map(item => item[periodKey]).filter(v => v != null))] as string[];
  const choices = columnChoices
    ?? ([...new Set(data.map(item => item[groupByKey]).filter(v => v != null && v !== 'undefined'))] as string[]);

  const tableData = periods.map(period => {
    const periodItems = data.filter(item => item[periodKey] === period);
    const row: any = { [periodKey]: period };
    choices.forEach(choice => { row[choice] = 0; });
    periodItems.forEach(item => {
      const key = item[groupByKey];
      if (key != null && String(key) !== 'undefined') {
        row[String(key)] = (row[String(key)] ?? 0) + 1;
      }
    });
    if (statusField) {
      row['Total Open'] = periodItems.filter(item => item[statusField] !== 'Closed').length;
      row['Total Closed'] = periodItems.filter(item => item[statusField] === 'Closed').length;
    }
    return row;
  });

  const allKeys = tableData.length > 0 ? Object.keys(tableData[0]) : [];
  const columnKeys = allKeys.map((key, index) => ({ ColumnName: key, Order: index }));

  return { tableData, columnKeys };
}

export function buildKpiSummary(
  data: any[],
  statusField: string | null,
  dueDateField: string | null,
  today: Date = new Date(),
): { total: number; open: number; closed: number; escalated: number; overdue: number } {
  const normalizedToday = new Date(today);
  normalizedToday.setHours(0, 0, 0, 0);

  if (!statusField) {
    return { total: data.length, open: 0, closed: 0, escalated: 0, overdue: 0 };
  }

  const open = data.filter(item => item[statusField] !== 'Closed' && item[statusField] !== 'Escalated').length;
  const closed = data.filter(item => item[statusField] === 'Closed').length;
  const escalated = data.filter(item => item[statusField] === 'Escalated').length;

  let overdue = 0;
  if (dueDateField) {
    overdue = data.filter(item => {
      if (!item[dueDateField]) return false;
      if (item[statusField] === 'Closed' || item[statusField] === 'Escalated') return false;
      const [d, m, y] = item[dueDateField].split('/');
      const due = new Date(+y, +m - 1, +d);
      return due < normalizedToday;
    }).length;
  }

  return { total: data.length, open, closed, escalated, overdue };
}
